import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import {
  ArrowRight, ShieldCheck, Star, Lightning, Lock, CurrencyDollar, Checks, Users, CalendarCheck, Sparkle, Storefront, EnvelopeSimple, Headset,
  Buildings, ForkKnife, MusicNotes, Camera, Palette, Cake, Diamond, Briefcase, House, VideoCamera,
  Martini, Flower, Lightbulb, Guitar, PersonSimpleThrow, Microphone, Broom, Headphones,
  DeviceMobile, CreditCard, Robot,
} from '@phosphor-icons/react';
import Navbar from '@/components/Navbar';
import Reveal from '@/components/Reveal';
import { TrustBar } from '@/components/TrustBadges';
import { invitesApi } from '@/lib/invitesApi';
import { EVENT_TYPE_SERVICES } from '@/lib/serviceCategories';
import { providerPrice } from '@/lib/pricing';
import { openSupportForm, supportMailto, SUPPORT_EMAIL } from '@/components/SupportContactModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ═══ Scroll-fading editorial photo (fades in entering view, fades out leaving) ═══
function ScrollPhoto({ src, alt, label, icon, className = '' }) {
  const ref = useRef(null);
  const [ratio, setRatio] = useState(0);
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setReduced(true); setRatio(1);
      return;
    }
    const obs = new IntersectionObserver(
      ([e]) => setRatio(e.intersectionRatio),
      { threshold: Array.from({ length: 21 }, (_, i) => i / 20) }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const o = Math.min(1, ratio * 1.5);
  return (
    <div
      ref={ref}
      className={`relative rounded-2xl overflow-hidden shadow-lift ${className}`}
      style={{
        opacity: o,
        transform: reduced ? 'none' : `translateY(${(1 - o) * 24}px) scale(${0.97 + o * 0.03})`,
        transition: 'opacity 0.35s ease-out, transform 0.35s ease-out',
      }}
    >
      <img src={src} alt={alt} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
      {/* Blue palette elements: duotone wash, caption pill, accent edge */}
      <div className="absolute inset-0 bg-gradient-to-tr from-primary/50 via-primary/10 to-transparent pointer-events-none" />
      <div className="absolute top-0 left-0 right-0 h-1 bg-primary/80" />
      <div className="absolute bottom-4 left-4 inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-full px-4 py-2 shadow-soft">
        {icon}
        <span className="text-xs font-semibold">{label}</span>
      </div>
    </div>
  );
}

// ═══ Self-playing AI planner demo (hero section) ═══
const DEMO_MSG = "Birthday party for 40 people, $3,500 budget. I need a DJ, catering and a photographer!";
const DEMO_CARDS = [
  { name: 'Your DJ', cat: '4 hours', price: '$480', initials: 'DJ' },
  { name: 'Catering', cat: '40 guests', price: '$1,000', initials: 'CA' },
  { name: 'Photographer', cat: '3 hours', price: '$540', initials: 'PH' },
];
const DEMO_TOTAL = 2141;
const DEMO_BUDGET = 3500;

function AIPlannerDemo() {
  const [typed, setTyped] = useState('');
  const [step, setStep] = useState(0); // 1 thinking, 2 reply, 3-5 cards, 6 total
  const [total, setTotal] = useState(0);
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (mq?.matches) { setReduced(true); setTotal(DEMO_TOTAL); return; }

    let timers = [];
    let typeInterval;
    const run = () => {
      timers.forEach(clearTimeout);
      timers = [];
      setTyped(''); setStep(0); setTotal(0);
      let i = 0;
      typeInterval = setInterval(() => {
        i++;
        setTyped(DEMO_MSG.slice(0, i));
        if (i >= DEMO_MSG.length) {
          clearInterval(typeInterval);
          timers.push(setTimeout(() => setStep(1), 400));
          timers.push(setTimeout(() => setStep(2), 1900));
          timers.push(setTimeout(() => setStep(3), 2500));
          timers.push(setTimeout(() => setStep(4), 2950));
          timers.push(setTimeout(() => setStep(5), 3400));
          timers.push(setTimeout(() => setStep(6), 4000));
          timers.push(setTimeout(run, 9500));
        }
      }, 26);
    };
    run();
    return () => { clearInterval(typeInterval); timers.forEach(clearTimeout); };
  }, []);

  // Count the total up once it appears
  useEffect(() => {
    if (step < 6 || reduced) return;
    let v = 0;
    const int = setInterval(() => {
      v += 106;
      if (v >= DEMO_TOTAL) { v = DEMO_TOTAL; clearInterval(int); }
      setTotal(v);
    }, 26);
    return () => clearInterval(int);
  }, [step, reduced]);

  const done = (n) => reduced || step >= n;

  // Every element stays mounted so the demo never changes size — steps only
  // toggle opacity, so content fades in place instead of expanding the card.
  const reveal = (n) => `transition-all duration-500 ${done(n) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1'}`;

  return (
    <div className="block space-y-3" aria-hidden="true" data-testid="ai-demo">
      {/* User message — typewriter */}
      <div className="bg-card border border-border/70 rounded-2xl rounded-bl-md p-4 text-sm text-foreground/80 shadow-soft min-h-[4.5rem]">
        "{reduced ? DEMO_MSG : typed}"
        {!reduced && typed.length < DEMO_MSG.length && <span className="text-primary animate-pulse">|</span>}
      </div>

      {/* AI reply bubble — always reserved; shows thinking dots until the plan is ready */}
      <div className={`ml-10 bg-primary text-primary-foreground rounded-2xl rounded-br-md px-4 text-sm shadow-soft min-h-[4.25rem] flex items-center transition-opacity duration-500 ${done(1) ? 'opacity-100' : 'opacity-0'}`}>
        {done(2) ? (
          <span>Here's your complete plan — 3 perfect matches, all within budget:</span>
        ) : (
          <span className="flex items-center gap-1.5">
            {[0, 1, 2].map(d => <span key={d} className="w-1.5 h-1.5 rounded-full bg-white/80 animate-bounce" style={{ animationDelay: `${d * 150}ms` }} />)}
          </span>
        )}
      </div>

      {/* Provider cards — always in layout, fade in one by one */}
      <div className="ml-10 space-y-2">
        {DEMO_CARDS.map((c, i) => (
          <div key={c.name} className={`bg-card border border-border/70 rounded-[0.625rem] px-3.5 py-2.5 shadow-soft flex items-center justify-between ${reveal(3 + i)}`}>
            <div className="flex items-center gap-2.5">
              <span className="w-7 h-7 rounded-full bg-secondary text-primary text-[10px] font-semibold flex items-center justify-center shrink-0">{c.initials}</span>
              <div>
                <p className="text-xs font-semibold text-foreground leading-tight">{c.name}</p>
                <p className="text-[10px] text-muted-foreground">{c.cat}</p>
              </div>
            </div>
            <span className="text-xs font-semibold text-foreground tabular-nums shrink-0">{c.price}</span>
          </div>
        ))}
      </div>

      {/* Total — always in layout, fades in and counts up */}
      <div className={`ml-10 flex items-center justify-between bg-foreground text-background rounded-[0.625rem] px-4 py-3 shadow-soft ${reveal(6)}`}>
        <div>
          <p className="text-[10px] uppercase tracking-wider text-background/60">Plan total</p>
          <p className="text-xl font-display font-semibold tabular-nums">${total.toLocaleString()}</p>
        </div>
        <div className="text-right">
          <p className="text-[10px] text-background/60 mb-1">Under budget by ${(DEMO_BUDGET - DEMO_TOTAL).toLocaleString()}</p>
          <span className="inline-flex items-center gap-1 bg-primary/15 text-background px-3 py-1 text-[11px] font-semibold rounded-full">Book all <ArrowRight size={11} weight="bold" /></span>
        </div>
      </div>
    </div>
  );
}

// Every service category on the marketplace (matches provider signup + demo catalog)
const ALL_SERVICES = [
  { name: 'Venues', img: 'https://images.unsplash.com/photo-1519167758481-83f550bb49b3?w=600&h=450&fit=crop' },
  { name: 'Caterer', img: 'https://images.unsplash.com/photo-1555244162-803834f70033?w=600&h=450&fit=crop' },
  { name: 'Private chef', img: 'https://images.unsplash.com/photo-1556910103-1c02745aae4d?w=600&h=450&fit=crop' },
  { name: 'Food truck', img: 'https://images.unsplash.com/photo-1565123409695-7b5ef63a2efb?w=600&h=450&fit=crop' },
  { name: 'Cake designer', img: 'https://images.unsplash.com/photo-1578985545062-69928b1d9587?w=600&h=450&fit=crop' },
  { name: 'Bartender', img: 'https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?w=600&h=450&fit=crop' },
  { name: 'DJ', img: 'https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=600&h=450&fit=crop' },
  { name: 'Live band', img: 'https://images.unsplash.com/photo-1501386761578-eac5c94b800a?w=600&h=450&fit=crop' },
  { name: 'Live musicians', img: 'https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=600&h=450&fit=crop' },
  { name: 'Dancers', img: 'https://images.unsplash.com/photo-1508700115892-45ecd05ae2ad?w=600&h=450&fit=crop' },
  { name: 'Performer', img: 'https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?w=600&h=450&fit=crop' },
  { name: 'MC / Host', img: 'https://images.unsplash.com/photo-1475721027785-f74eccf877e2?w=600&h=450&fit=crop' },
  { name: 'Kids entertainment', img: 'https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=600&h=450&fit=crop' },
  { name: 'Photobooth', img: 'https://images.unsplash.com/photo-1524368535928-5b5e00ddc76b?w=600&h=450&fit=crop' },
  { name: 'Photographer', img: 'https://images.unsplash.com/photo-1542038784456-1ea8e935640e?w=600&h=450&fit=crop' },
  { name: 'Videographer', img: 'https://images.unsplash.com/photo-1579632652768-6cb9dcf85912?w=600&h=450&fit=crop' },
  { name: 'Event decorator', img: 'https://images.unsplash.com/photo-1478146059778-26028b07395a?w=600&h=450&fit=crop' },
  { name: 'Florist', img: 'https://images.unsplash.com/photo-1465495976277-4387d4b0b4c6?w=600&h=450&fit=crop' },
  { name: 'Lighting & AV', img: 'https://images.unsplash.com/photo-1504509546545-e000b4a62425?w=600&h=450&fit=crop' },
  { name: 'Rentals', img: 'https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?w=600&h=450&fit=crop' },
  { name: 'Servers', img: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=600&h=450&fit=crop' },
  { name: 'Security', img: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?w=600&h=450&fit=crop' },
  { name: 'Cleaner', img: 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=600&h=450&fit=crop' },
  { name: 'Makeup artist', img: 'https://images.unsplash.com/photo-1487412947147-5cebf100ffc2?w=600&h=450&fit=crop' },
  { name: 'Barber', img: 'https://images.unsplash.com/photo-1503951914875-452162b0f3f1?w=600&h=450&fit=crop' },
];

// The six staples shown on the homepage — the rest live behind "and more"
const TOP_SERVICES = ['Venues', 'Caterer', 'DJ', 'Photographer', 'Bartender', 'Event decorator']
  .map(name => ALL_SERVICES.find(s => s.name === name));

// Browse filters by regex-matching a provider's stored `category`, so these links must use
// the names providers are actually saved under (the singular display names below) — NOT the
// plural /api/categories taxonomy ("DJs", "Catering"), which matches zero providers.
// Only genuine aliases need an entry; everything else maps to itself.
const SERVICE_CAT = {
  'Catering': 'Caterer', 'DJs': 'DJ', 'Photographers': 'Photographer',
  'Bartenders': 'Bartender', 'Event Decor': 'Event decorator', 'Private Chefs': 'Private chef',
  'Handyman': 'Electrician|Plumber',
};
const catHref = (name) => `/browse?category=${encodeURIComponent(SERVICE_CAT[name] || name)}`;

const FEATURED_ICON = { 'Venues': Buildings, 'DJ': Headphones, 'DJs': Headphones, 'Caterer': ForkKnife, 'Catering': ForkKnife, 'Private chef': ForkKnife, 'Photographer': Camera, 'Photographers': Camera, 'Videographer': VideoCamera, 'Bartender': Martini, 'Event decorator': Palette, 'Florist': Flower, 'Lighting & AV': Lightbulb, 'Live musicians': Guitar, 'Dancers': PersonSimpleThrow, 'Performer': Microphone, 'MC / Host': Microphone, 'Cleaner': Broom };

const CATEGORIES = [
  { id: 'venues', name: 'Venues', Icon: Buildings, img: 'https://images.unsplash.com/photo-1519167758481-83f550bb49b3?w=400&h=300&fit=crop' },
  { id: 'catering', name: 'Catering', Icon: ForkKnife, img: 'https://images.unsplash.com/photo-1555244162-803834f70033?w=400&h=300&fit=crop' },
  { id: 'entertainment', name: 'Entertainment', Icon: MusicNotes, img: 'https://images.unsplash.com/photo-1763651961295-27333a96f427?w=400&h=300&fit=crop', link: catHref('DJ') },
  { id: 'photo-video', name: 'Photography', Icon: Camera, img: 'https://images.unsplash.com/photo-1758613653843-87c253aea8cd?w=400&h=300&fit=crop', link: catHref('Photographer') },
  { id: 'event-decor', name: 'Decor', Icon: Palette, img: 'https://images.unsplash.com/photo-1478146059778-26028b07395a?w=400&h=300&fit=crop', link: catHref('Event decorator') },
  { id: 'staffing', name: 'Staffing', Icon: Users, img: 'https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?w=400&h=300&fit=crop', link: catHref('Bartender') },
];

const EXAMPLE_SEARCHES = [
  'Birthday party for 30 people under $2,000',
  'Wedding photographer + videographer for 100 guests',
  'Private chef for dinner party of 12',
  'Corporate event DJ + catering for 80 guests',
  'Deep cleaning after house party',
];

// Event types with real photography — makes the landing read like an event site.
// `cat` must use CANONICAL provider categories (backend taxonomy); browse regex-matches,
// so several categories can be OR'd together with "|".
const EVENT_TYPES = [
  { name: 'Weddings', blurb: 'Venue, florals, photo & more', cat: EVENT_TYPE_SERVICES.weddings.join('|'), img: 'https://images.unsplash.com/photo-1527529482837-4698179dc6ce?w=800&q=80&auto=format&fit=crop' },
  { name: 'Birthdays', blurb: 'Cake, decor & entertainment', cat: EVENT_TYPE_SERVICES.birthdays.join('|'), img: 'https://images.unsplash.com/photo-1482731910308-31e96e5d1d28?w=800&q=80&auto=format&fit=crop' },
  { name: 'Corporate', blurb: 'Venues, catering & staffing', cat: EVENT_TYPE_SERVICES.corporate.join('|'), img: 'https://images.unsplash.com/photo-1515169067868-5387ec356754?w=800&q=80&auto=format&fit=crop' },
  { name: 'House parties', blurb: 'DJ, bartender & cleanup', cat: EVENT_TYPE_SERVICES.houseparties.join('|'), img: 'https://images.unsplash.com/photo-1517457373958-b7bdd4587205?w=800&q=80&auto=format&fit=crop' },
];

export default function LandingPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  // Invitations feature — sample designs + "Create an Invitation" CTA
  const [inviteSamples, setInviteSamples] = useState([]);
  useEffect(() => {
    invitesApi.templates({ format: 'card', premium: false, limit: 5 })
      .then((d) => setInviteSamples(d.templates || []))
      .catch(() => {});
  }, []);
  const goInvitations = async () => {
    if (!user) { navigate('/auth?mode=register'); return; }
    try {
      const ev = await invitesApi.createEvent({ source: 'standalone', title: `${user.name || 'My'}'s Event` }, token);
      navigate(`/events/${ev.event_id}/invitations`);
    } catch { navigate('/auth'); }
  };

  // "Apply as a provider" — send them to the setup form directly when we can.
  // Logged-out visitors go through auth (which lands on the same form afterward);
  // existing providers go to their dashboard.
  const goProvider = () => {
    if (!user) navigate('/auth?mode=register&role=provider');
    else if (user.role === 'provider') navigate('/provider/dashboard');
    else navigate('/provider/setup-profile');
  };
  const [searchIdx, setSearchIdx] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [typing, setTyping] = useState(true);

  // Typewriter effect for example searches
  useEffect(() => {
    const target = EXAMPLE_SEARCHES[searchIdx];
    if (typing) {
      if (searchText.length < target.length) {
        const t = setTimeout(() => setSearchText(target.slice(0, searchText.length + 1)), 45);
        return () => clearTimeout(t);
      } else {
        const t = setTimeout(() => setTyping(false), 2000);
        return () => clearTimeout(t);
      }
    } else {
      if (searchText.length > 0) {
        const t = setTimeout(() => setSearchText(searchText.slice(0, -1)), 25);
        return () => clearTimeout(t);
      } else {
        setSearchIdx((searchIdx + 1) % EXAMPLE_SEARCHES.length);
        setTyping(true);
      }
    }
  }, [searchText, typing, searchIdx]);

  // Real featured providers (top-rated, approved) — section hides if fewer than 3
  const [featured, setFeatured] = useState([]);
  useEffect(() => {
    axios.get(`${API}/providers`).then(r => {
      const provs = (r.data.providers || [])
        .sort((a, b) => (b.rating_avg || 0) - (a.rating_avg || 0))
        .slice(0, 6)
        .map(p => ({
          name: p.business_name,
          cat: p.category,
          Icon: FEATURED_ICON[p.category] || Sparkle,
          // Same priority as the Browse card: the vendor's cover photo leads,
          // then their first portfolio shot, then their avatar. (Previously only
          // looked at portfolio_images[0], so providers whose photo lives in
          // cover_image showed a blank emoji tile.)
          cover: p.cover_image || (p.portfolio_images || [])[0] || p.user?.avatar_url || null,
          coverFocus: (() => {
            const raw = p.cover_image || (p.portfolio_images || [])[0] || p.user?.avatar_url || null;
            return raw ? (p.image_focus || {})[raw] : null;
          })(),
          profileId: p.profile_id,
          // Single source of truth for amount + unit (matches Browse / profile).
          price: providerPrice(p).amount,
          unit: providerPrice(p).suffixShort,
          priceDisplay: providerPrice(p).display,
          isQuote: providerPrice(p).isQuote,
          rating: p.rating_avg || 0,
          reviews: p.review_count || 0,
          badge: p.verified ? 'Verified' : 'New',
          desc: p.description || '',
        }));
      setFeatured(provs);
    }).catch(() => {});
  }, []);

  // Hero chat → hand the message to the real AI planner (via auth if needed)
  const [chatMsg, setChatMsg] = useState('');
  const startPlan = (msg) => {
    const text = (msg || '').trim();
    if (!text) return;
    sessionStorage.setItem('bookr_pending_plan', text);
    navigate(user ? '/ai-planner' : '/auth?mode=register');
  };
  const handleChatSubmit = (e) => {
    e.preventDefault();
    startPlan(chatMsg);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      <Navbar />

      {/* ═══ HERO ═══ */}
      <section className="relative overflow-hidden bg-hero-wash" data-testid="hero-section">
        {/* Breathing aurora background */}
        <div className="aurora-blob aurora-1 w-[36rem] h-[36rem] -top-40 -right-24" aria-hidden="true" />
        <div className="aurora-blob aurora-2 w-[30rem] h-[30rem] top-24 -left-32" aria-hidden="true" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-16 md:pt-24 md:pb-20">
          {/* ── Two-column hero: editorial headline + live chat (left), self-playing demo (right) ── */}
          <div className="lg:grid lg:grid-cols-[1.05fr_0.95fr] lg:gap-14 xl:gap-20 lg:items-center">

            {/* LEFT — headline + the real interactive chat */}
            <div data-testid="hero-chat">
              {/* Eyebrow — the niche, stated plainly */}
              <div className="fade-up flex justify-center lg:justify-start" style={{ animationDelay: '40ms' }}>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 text-primary text-[11px] sm:text-xs font-bold uppercase tracking-widest px-3.5 py-1.5 mb-6">
                  <Sparkle size={13} weight="fill" className="text-accent-coral" /> AI Event Planner
                </span>
              </div>

              {/* Major headline — editorial serif at large scale */}
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-display font-semibold tracking-tight leading-[1.03] text-foreground mb-6 text-center lg:text-left fade-up" style={{ animationDelay: '80ms' }}>
                Plan your whole event{' '}<em className="italic text-primary">in one chat.</em>
              </h1>

              <p className="text-lg lg:text-xl text-muted-foreground leading-relaxed max-w-md mx-auto lg:mx-0 mb-9 text-center lg:text-left fade-up" style={{ animationDelay: '130ms' }}>
                Tell us the vibe, the date and the budget — our AI plans it and books real vendors who are free that day.
              </p>

              {/* The real interactive chat card */}
              <div className="relative fade-up text-left max-w-xl mx-auto lg:mx-0" style={{ animationDelay: '200ms' }}>
                {/* soft glow */}
                <div className="absolute -inset-4 rounded-[2.5rem] bg-primary/8 blur-2xl pointer-events-none" aria-hidden="true" />
                <div className="relative bg-card border border-border/70 rounded-[1.25rem] shadow-float overflow-hidden">
                  {/* Chat header */}
                  <div className="flex items-center gap-3 px-5 py-3.5 border-b border-border/60 bg-gradient-to-r from-secondary/70 to-secondary/25">
                    <span className="w-10 h-10 rounded-full bg-primary flex items-center justify-center shrink-0 shadow-soft">
                      <Sparkle size={17} weight="fill" className="text-primary-foreground" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-foreground leading-tight">Bookr AI Planner</p>
                      <p className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" /> Online — ready to plan
                      </p>
                    </div>
                    <span className="hidden sm:inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 shrink-0">
                      <Lightning size={11} weight="fill" /> Free
                    </span>
                  </div>

                  {/* Greeting + quick replies */}
                  <div className="px-5 py-5 space-y-3.5">
                    <div className="bg-secondary/60 rounded-2xl rounded-tl-md px-4 py-3 text-sm sm:text-[15px] text-foreground/85 w-fit max-w-[92%]">
                      Hi! I'm your event planner. What are you planning?
                    </div>
                    <div className="flex flex-wrap gap-2" data-testid="hero-chat-quick-replies">
                      {[
                        { Icon: Cake, label: 'Birthday party', msg: "I'm planning a birthday party" },
                        { Icon: Diamond, label: 'Wedding', msg: "I'm planning a wedding" },
                        { Icon: Briefcase, label: 'Corporate event', msg: "I'm planning a corporate event" },
                        { Icon: House, label: 'House party', msg: "I'm planning a house party" },
                      ].map((a, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => startPlan(a.msg)}
                          className="inline-flex items-center gap-1.5 rounded-full bg-secondary text-primary text-sm font-medium px-4 py-2.5 min-h-[42px] btn-anim hover:bg-primary hover:text-primary-foreground cursor-pointer"
                          data-testid={`quick-action-${i}`}
                        >
                          {a.Icon && <a.Icon size={16} weight="regular" />} {a.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Chat input */}
                  <form onSubmit={handleChatSubmit} className="px-4 pb-4" data-testid="hero-chat-form">
                    <div className="relative">
                      <input
                        type="text"
                        value={chatMsg}
                        onChange={e => setChatMsg(e.target.value)}
                        placeholder={searchText}
                        aria-label="Describe your event to the AI planner"
                        className="w-full bg-background border border-border rounded-full pl-5 pr-16 py-4 text-sm sm:text-base shadow-soft focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/50 transition-all duration-300 placeholder:text-muted-foreground/70"
                        data-testid="hero-chat-input"
                      />
                      <button type="submit" aria-label="Start planning" className="absolute right-2 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-primary text-primary-foreground flex items-center justify-center btn-anim shadow-soft hover:shadow-lift hover:bg-primary/90 cursor-pointer" data-testid="hero-chat-send">
                        <ArrowRight size={18} weight="bold" />
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>

            {/* RIGHT — the live, self-playing demo (the star). Hidden on mobile; chat card carries the hero there. */}
            <div className="hidden lg:block fade-up" style={{ animationDelay: '320ms' }} data-testid="hero-demo">
              <div className="relative">
                <div className="absolute -inset-5 rounded-[2rem] bg-primary/8 blur-2xl pointer-events-none" aria-hidden="true" />
                <div className="relative bg-card border border-border/70 rounded-[1.25rem] shadow-float p-5">
                  {/* faux app header */}
                  <div className="flex items-center gap-2.5 pb-4 mb-1 border-b border-border/60">
                    <span className="w-8 h-8 rounded-full bg-primary flex items-center justify-center shrink-0 shadow-soft">
                      <Sparkle size={14} weight="fill" className="text-primary-foreground" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-foreground leading-tight">Bookr AI Planner</p>
                      <p className="text-[10px] text-muted-foreground flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary inline-block animate-pulse" /> planning…
                      </p>
                    </div>
                    <span className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground">Live demo</span>
                  </div>
                  <div className="pt-3">
                    <AIPlannerDemo />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── What kind of event? — real photography so it reads as an event site ── */}
          <div className="mt-16 md:mt-20 max-w-5xl mx-auto fade-up" style={{ animationDelay: '260ms' }} data-testid="event-types">
            <p className="text-center text-[11px] font-medium uppercase tracking-widest text-muted-foreground mb-4">What are you celebrating?</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {EVENT_TYPES.map((e, i) => (
                <Link key={e.name} to={`/browse?category=${encodeURIComponent(e.cat)}`}
                  className="group relative rounded-2xl overflow-hidden aspect-[4/3] shadow-soft card-lift cursor-pointer"
                  data-testid={`event-type-${i}`}>
                  <img src={e.img} alt={e.name} loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground/85 via-foreground/25 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-3.5">
                    <p className="text-primary-foreground text-sm font-bold leading-tight drop-shadow">{e.name}</p>
                    <p className="text-primary-foreground/85 text-[11px] font-medium leading-snug">{e.blurb}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* Browse directly */}
          <div className="max-w-2xl mx-auto mt-9 fade-up" style={{ animationDelay: '300ms' }} data-testid="quick-actions">
            <p className="text-[11px] font-medium uppercase tracking-widest text-muted-foreground text-center mb-3">Or browse directly</p>
            <div className="flex flex-wrap gap-2 justify-center">
              {[
                { label: 'Find a DJ', to: catHref('DJ') },
                { label: 'Hire a photographer', to: catHref('Photographer') },
                { label: 'Book a venue', to: catHref('Venues') },
                { label: 'Catering for 50+', to: catHref('Caterer') },
                { label: 'Bartender for my party', to: catHref('Bartender') },
                { label: 'Event decorator', to: catHref('Event decorator') },
                { label: 'Private chef dinner', to: catHref('Private chef') },
              ].map((a, i) => (
                <Link key={i} to={a.to}
                  className="text-xs font-medium text-muted-foreground border border-border bg-card px-3.5 py-2 rounded-full btn-anim hover:bg-secondary hover:text-primary hover:border-primary/30 cursor-pointer"
                  data-testid={`quick-link-${i}`}>
                  {a.label}
                </Link>
              ))}
            </div>
          </div>

          <div className="max-w-3xl mx-auto">
            <TrustBar className="mt-4 fade-up" />
          </div>
        </div>
      </section>

      {/* ═══ AI PLANNER — PROMINENT SECTION (editorial statement; the live demo lives in the hero) ═══ */}
      <Reveal as="section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28" data-testid="ai-planner-hero">
        <div className="relative overflow-hidden border border-border/70 rounded-[1.25rem] bg-gradient-to-br from-secondary via-card to-secondary/60 shadow-soft px-8 py-16 md:px-16 md:py-24 text-center">
          <div className="max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 mb-6">
              <Lightning size={13} weight="fill" className="text-primary" />
              <span className="text-[11px] font-semibold uppercase tracking-widest text-primary">AI Event Planner</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-display font-semibold tracking-tight text-foreground mb-5 leading-[1.08]">
              Plan your entire event in one conversation
            </h2>
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-9 max-w-xl mx-auto">
              Tell our AI what you need — it finds the venue, catering, DJ, photographer, and everything else. One click to book it all.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to={user ? "/ai-planner" : "/auth"} className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-xl px-7 h-12 text-sm font-semibold btn-anim shadow-soft hover:shadow-lift hover:bg-primary/90 cursor-pointer" data-testid="ai-hero-cta">
                <Lightning size={15} weight="fill" /> Start planning
              </Link>
              <Link to="/browse" className="inline-flex items-center gap-2 border border-border bg-card text-foreground rounded-xl px-7 h-12 text-sm font-medium btn-anim hover:bg-secondary/60 cursor-pointer">
                Or browse manually
              </Link>
            </div>
          </div>
        </div>
      </Reveal>

      {/* ═══ OFFER YOUR OWN SERVICES — vendor-facing, deliberately its own look ═══ */}
      <Reveal as="section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6 md:pb-10" data-testid="provider-section">
        <div className="relative overflow-hidden rounded-3xl shadow-soft"
          style={{ border: '2px solid hsl(var(--coral) / 0.28)', background: 'linear-gradient(135deg, hsl(var(--coral) / 0.09), hsl(var(--card)) 55%, hsl(var(--coral) / 0.05))' }}>
          <div className="grid md:grid-cols-[1.05fr_0.95fr]">
            <div className="p-10 md:p-14">
              <span className="inline-flex items-center gap-2 rounded-full px-3 py-1 mb-4" style={{ background: 'hsl(var(--coral) / 0.12)' }}>
                <Storefront size={13} weight="fill" className="text-accent-coral" />
                <span className="text-[11px] font-bold uppercase tracking-widest text-accent-coral">For vendors &amp; pros</span>
              </span>
              <h2 className="text-4xl md:text-5xl font-display font-semibold tracking-tight text-foreground mb-4 leading-[1.08]">
                Offer your own services
              </h2>
              <p className="text-base text-muted-foreground leading-relaxed mb-6 max-w-md">
                DJ, photographer, caterer, venue, electrician — list what you do and get matched with customers who are ready to book. Free to join, you set your own prices and availability, and you only pay when you get paid.
              </p>
              <div className="grid sm:grid-cols-3 gap-3 mb-7">
                {[
                  { icon: <CurrencyDollar size={15} weight="fill" />, t: 'Free to join', s: 'No listing fees' },
                  { icon: <CalendarCheck size={15} weight="fill" />, t: 'You set the terms', s: 'Prices & availability' },
                  { icon: <ShieldCheck size={15} weight="fill" />, t: 'Paid securely', s: 'Stripe payouts' },
                ].map((f) => (
                  <div key={f.t} className="flex items-start gap-2.5">
                    <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 text-accent-coral" style={{ background: 'hsl(var(--coral) / 0.12)' }}>{f.icon}</span>
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-foreground leading-tight">{f.t}</span>
                      <span className="block text-[11px] text-muted-foreground">{f.s}</span>
                    </span>
                  </div>
                ))}
              </div>
              <button onClick={goProvider} data-testid="provider-section-cta"
                className="inline-flex items-center gap-2.5 rounded-full px-7 h-12 text-sm sm:text-base font-semibold text-white btn-anim shadow-soft hover:shadow-lift cursor-pointer"
                style={{ background: 'hsl(var(--coral))' }}>
                <Storefront size={18} weight="fill" /> Offer your services <ArrowRight size={17} weight="bold" />
              </button>
            </div>
            {/* Photo — a real pro at work */}
            <div className="relative min-h-[220px] md:min-h-[400px]">
              <img src="https://images.unsplash.com/photo-1769230385107-bc6eaa7a123e?w=900&q=80&auto=format&fit=crop"
                alt="An event photographer shooting a reception" loading="lazy"
                className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 hidden md:block" style={{ background: 'linear-gradient(90deg, hsl(var(--card)) 0%, transparent 42%)' }} />
            </div>
          </div>
        </div>
      </Reveal>
      {/* ═══ STATS BAND ═══ */}
      <Reveal as="section" className="border-y border-border bg-card" data-testid="trusted-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="text-center md:text-left">
            <p className="text-xs font-medium uppercase tracking-widest text-primary mb-1">Just launched</p>
            <p className="text-sm text-muted-foreground">Be among the first to plan and book events the new way — anywhere.</p>
          </div>
          <div className="flex flex-wrap justify-center gap-x-12 gap-y-4 md:gap-x-16">
            {[
              { value: '6%', label: 'Flat fee — no subscriptions' },
              { value: '100%', label: 'Escrow-protected payments' },
              { value: '24h', label: 'Provider application review' },
            ].map(s => (
              <div key={s.label} className="text-center">
                <div className="text-3xl sm:text-4xl font-display font-semibold text-foreground">{s.value}</div>
                <div className="text-[11px] text-muted-foreground mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </Reveal>

      {/* ═══ ALL SERVICES ═══ */}
      <Reveal as="section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 md:pt-28" data-testid="all-services-section">
        <div className="text-center mb-12 max-w-2xl mx-auto">
          <p className="text-sm font-medium tracking-widest uppercase text-primary mb-2">Also on Bookr.ai · À la carte</p>
          <h2 className="text-4xl md:text-5xl font-display font-semibold tracking-tight text-foreground leading-[1.08]">Just need one pro? Book them on their own.</h2>
          <p className="text-base text-muted-foreground leading-relaxed mt-4">Bookr isn't only for big events. Book any individual service à la carte — a DJ, photographer or bartender for the party, or a cleaner, makeup artist, barber, even an <span className="text-foreground font-medium">electrician</span> or handyman when you just need one job done. Same trusted marketplace, one booking at a time.</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {TOP_SERVICES.map((s, i) => (
            <Link key={s.name} to={catHref(s.name)}
              className="group relative rounded-xl overflow-hidden aspect-[4/3] shadow-soft card-lift cursor-pointer fade-up"
              style={{ animationDelay: `${i * 40}ms` }}
              data-testid={`service-tile-${i}`}>
              <img src={s.img} alt={s.name}
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground/70 via-foreground/10 to-transparent" />
              <span className="absolute bottom-2 left-2.5 right-2.5 text-primary-foreground text-xs font-semibold leading-tight drop-shadow">{s.name}</span>
            </Link>
          ))}
        </div>
        {/* Breadth of individual services — event pros AND everyday help */}
        <div className="flex flex-wrap justify-center gap-2 mt-6" data-testid="service-pills">
          {['DJ', 'Photographer', 'Bartender', 'Private chef', 'Cleaner', 'Makeup artist', 'Barber', 'Electrician', 'Handyman', 'Security', 'Florist'].map((name) => (
            <Link key={name} to={catHref(name)}
              className="text-xs font-medium text-muted-foreground border border-border bg-card px-3.5 py-2 rounded-full btn-anim hover:bg-secondary hover:text-primary hover:border-primary/30 cursor-pointer">
              {name}
            </Link>
          ))}
        </div>
        <div className="text-center mt-5">
          <Link to="/browse" className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary/80 transition-colors cursor-pointer" data-testid="all-services-more">
            Browse every service <ArrowRight size={14} />
          </Link>
        </div>
      </Reveal>

      {/* ═══ EVENT COORDINATOR — its own identity: dark "mission control" ═══ */}
      <Reveal as="section" className="relative overflow-hidden" data-testid="coordinator-section"
        style={{ background: 'linear-gradient(150deg, hsl(218 48% 15%), hsl(218 48% 9%))' }}>
        {/* faint venue photo for depth */}
        <img src="https://images.unsplash.com/photo-1677768062274-fdd45caac233?w=1600&q=80&auto=format&fit=crop"
          alt="" aria-hidden="true" loading="lazy"
          className="absolute inset-0 w-full h-full object-cover" style={{ opacity: 0.14 }} />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(150deg, hsl(218 48% 13% / 0.78), hsl(218 48% 9% / 0.94))' }} />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          <div className="grid md:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* Live dashboard mockup — a light panel floating on dark */}
            <div className="order-2 md:order-1">
              <div className="bg-card rounded-[1.25rem] shadow-float p-6 max-w-sm mx-auto">
                <div className="flex items-center justify-between pb-3 mb-3 border-b border-border/60">
                  <div>
                    <div className="text-3xl font-display font-semibold text-primary leading-none tabular-nums">12</div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">days to go</div>
                  </div>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 text-emerald-600 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" /> On track
                  </span>
                </div>
                {[
                  { n: 'Le Loft Mile-End', c: 'Venue', s: 'Confirmed' },
                  { n: 'DJ Nova', c: 'DJ', s: 'Confirmed' },
                  { n: 'Taco Libre', c: 'Catering', s: 'Balance due Jul 20' },
                ].map(v => (
                  <div key={v.n} className="flex items-center justify-between py-1.5 text-xs">
                    <span className="text-foreground font-medium">{v.n} <span className="text-muted-foreground font-normal">· {v.c}</span></span>
                    <span className="text-[10px] font-medium text-primary shrink-0 ml-2">{v.s}</span>
                  </div>
                ))}
                <div className="mt-3 flex items-start gap-2">
                  <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0"><Sparkle size={11} weight="fill" /></span>
                  <div className="bg-secondary/60 rounded-2xl rounded-tl-md px-3 py-2 text-[11px] text-foreground/80 leading-relaxed">Here's your run-of-show — 5:00 setup, 6:00 guests arrive, 6:30 dinner, 8:00 dancing. Want me to send it to your vendors?</div>
                </div>
              </div>
            </div>

            {/* Text — light on dark */}
            <div className="order-1 md:order-2">
              <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 mb-5" style={{ background: 'rgba(255,255,255,0.10)' }}>
                <CalendarCheck size={13} weight="fill" className="text-white" />
                <span className="text-[11px] font-semibold uppercase tracking-widest text-white/90">Step 2 · After you book</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-display font-semibold tracking-tight text-white mb-5 leading-[1.08]">
                Your AI Event Coordinator takes it from here
              </h2>
              <p className="text-sm sm:text-base leading-relaxed mb-7" style={{ color: 'rgba(255,255,255,0.72)' }}>
                Booking is only half of it. Once your vendors are locked in, your coordinator confirms every detail, builds the run-of-show, stays ahead of payment deadlines and handles the day itself — a planner in your pocket, any time.
              </p>
              <div className="grid sm:grid-cols-2 gap-3 mb-8">
                {[
                  { icon: <Checks size={15} weight="bold" />, t: 'Confirm vendors in writing' },
                  { icon: <CalendarCheck size={15} weight="fill" />, t: 'Build your run-of-show' },
                  { icon: <CurrencyDollar size={15} weight="fill" />, t: 'Track payment deadlines' },
                  { icon: <Users size={15} weight="fill" />, t: 'Plan day-of logistics' },
                ].map(f => (
                  <div key={f.t} className="flex items-center gap-2.5">
                    <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 text-white" style={{ background: 'rgba(255,255,255,0.12)' }}>{f.icon}</span>
                    <span className="text-sm font-medium text-white/90">{f.t}</span>
                  </div>
                ))}
              </div>
              <Link to={user ? '/coordinator' : '/auth?mode=register'} className="inline-flex items-center gap-2 bg-white text-foreground rounded-xl px-6 h-12 text-sm font-semibold btn-anim shadow-lift hover:bg-white/90 cursor-pointer" data-testid="coordinator-section-cta">
                <Sparkle size={15} weight="fill" className="text-primary" /> Meet your coordinator
              </Link>
            </div>
          </div>
        </div>
      </Reveal>

      {/* ═══ INVITATIONS — its own identity: warm paper / stationery ═══ */}
      <Reveal as="section" className="relative overflow-hidden border-y" data-testid="invitations-section"
        style={{ borderColor: 'hsl(var(--coral) / 0.2)', background: 'linear-gradient(160deg, #FFF8F5, #FDF1EC 55%, #FFF6F8)' }}>
        {/* soft paper texture dots */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true"
          style={{ backgroundImage: 'radial-gradient(hsl(var(--coral) / 0.18) 1px, transparent 1px)', backgroundSize: '22px 22px', opacity: 0.5 }} />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full px-3 py-1 mb-4" style={{ background: 'hsl(var(--coral) / 0.14)' }}>
                <EnvelopeSimple size={13} weight="fill" className="text-accent-coral" />
                <span className="text-[11px] font-bold uppercase tracking-widest text-accent-coral">Step 3 · Invitations</span>
              </span>
              <h2 className="text-3xl md:text-5xl font-display font-semibold tracking-tight text-foreground leading-[1.1]">
                Send invitations they <em className="italic text-accent-coral">can't wait</em> to open
              </h2>
              <p className="text-base sm:text-lg text-muted-foreground leading-relaxed mt-4 max-w-lg">
                Design and send beautiful invitations without leaving Bookr.ai. Hundreds of designer templates plus creative formats — animated envelope <strong className="text-foreground font-semibold">Cards</strong>, <strong className="text-foreground font-semibold">Now Playing</strong> music cards, vinyl, boarding passes and polaroids. Customize every color and font, attach photos and a song, then track every RSVP in real time.
              </p>
              <ul className="mt-5 space-y-2.5">
                {['Hundreds of templates + creative card formats', 'Photos, video & an attached song like an IG post', 'RSVP tracking, reminders & guest check-in'].map((f) => (
                  <li key={f} className="flex items-center gap-2.5 text-sm text-foreground/80">
                    <span className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 text-accent-coral" style={{ background: 'hsl(var(--coral) / 0.14)' }}>
                      <Checks size={12} weight="bold" />
                    </span>
                    {f}
                  </li>
                ))}
              </ul>
              <div className="flex flex-wrap gap-3 mt-7">
                <button onClick={goInvitations} className="inline-flex items-center gap-2 rounded-full px-6 h-12 text-base font-semibold text-white btn-anim shadow-soft hover:shadow-lift cursor-pointer" style={{ background: 'hsl(var(--coral))' }} data-testid="create-invitation-cta">
                  Create an Invitation <ArrowRight size={17} weight="bold" />
                </button>
                <Link to="/i/demo" className="inline-flex items-center gap-2 bg-card border-2 text-foreground rounded-full px-6 h-12 text-base font-semibold btn-anim hover:bg-white cursor-pointer" style={{ borderColor: 'hsl(var(--coral) / 0.35)' }}>
                  See a live invite
                </Link>
              </div>
            </div>

            {/* Stationery photo + the template fan layered on top */}
            <div className="relative" data-testid="invite-samples">
              <div className="relative rounded-3xl overflow-hidden shadow-float">
                <img src="https://images.unsplash.com/photo-1758825178518-ca48833a6c57?w=1000&q=80&auto=format&fit=crop"
                  alt="Invitation stationery flat-lay with envelopes and ribbon" loading="lazy"
                  className="w-full h-[300px] sm:h-[360px] object-cover" />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(255,248,245,0.18), rgba(253,241,236,0.55))' }} />
              </div>
              {/* fan of live templates sitting on the photo */}
              <div className="absolute inset-x-0 bottom-[-26px] flex justify-center items-end gap-3">
                {(inviteSamples.length ? inviteSamples.slice(0, 3) : [null, null, null]).map((t, i) => {
                  const c = t?.colors || { bg: ['#FBEFF1', '#FFF3F0', '#EEF3EC'][i], ink: '#26323f', accent: ['#D98A9E', '#FF6D57', '#7A9B78'][i] };
                  const tilt = [-8, 0, 8][i];
                  const lift = [18, 0, 18][i];
                  return (
                    <div key={i} className="relative rounded-2xl shadow-float overflow-hidden border border-black/5"
                      style={{ width: 150, aspectRatio: '3/4', background: c.bg, transform: `rotate(${tilt}deg) translateY(${lift}px)`, zIndex: i === 1 ? 2 : 1 }}>
                      <div className="absolute inset-3 rounded-lg" style={{ border: `1px solid ${c.accent}`, opacity: 0.4 }} />
                      <div className="h-full flex flex-col items-center justify-center px-4 text-center">
                        <div style={{ fontFamily: t?.font?.heading || "'Outfit', sans-serif", color: c.ink, fontWeight: 600, fontSize: 18 }}>{t?.layout?.blocks?.[0]?.text || "You're Invited"}</div>
                        <div style={{ color: c.accent, fontSize: 10, marginTop: 4, fontWeight: 600 }}>{t?.category_name || 'Celebration'}</div>
                        <div className="mt-3 w-7 h-7 rounded-full flex items-center justify-center text-white" style={{ background: c.accent }}><Sparkle size={13} weight="fill" /></div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="h-10" />
            </div>
          </div>
        </div>
      </Reveal>

      {/* ═══ FEATURED PROVIDERS (real data — hidden until at least 3 exist) ═══ */}
      {featured.length >= 3 && (
      <Reveal as="section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28" data-testid="popular-section">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="text-sm font-medium tracking-widest uppercase text-primary mb-2">Our providers</p>
            <h2 className="text-4xl md:text-5xl font-display font-semibold tracking-tight text-foreground">Featured providers</h2>
          </div>
          <Link to="/browse" className="text-sm font-medium text-primary hover:text-primary/80 transition-colors hidden md:flex items-center gap-1 cursor-pointer">
            View all <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {featured.map((p, i) => (
            <Link key={i} to={p.profileId ? `/provider/${p.profileId}` : '/browse'} className="group bg-card rounded-2xl border border-border/70 shadow-soft card-lift overflow-hidden cursor-pointer fade-up" style={{ animationDelay: `${i * 50}ms` }} data-testid={`popular-${i}`}>
              {/* Cover photo (vendor's cover / first portfolio image). The icon
                  sits underneath as a placeholder — if the photo is missing OR
                  fails to load (onError), it hides and the icon shows through,
                  so a broken URL never leaves ugly alt text on the card. */}
              <div className="relative h-28 bg-secondary/60 overflow-hidden">
                <div className="absolute inset-0 flex items-center justify-center text-primary/40" aria-hidden="true">{p.Icon && <p.Icon size={30} weight="regular" />}</div>
                {p.cover && (
                  <img
                    src={p.cover.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${p.cover}` : p.cover}
                    alt={p.name}
                    loading="lazy"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    style={Array.isArray(p.coverFocus) && p.coverFocus.length === 2 ? { objectPosition: `${p.coverFocus[0]}% ${p.coverFocus[1]}%` } : undefined}
                    className="relative w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                )}
                <span className="absolute top-2 right-2 rounded-full bg-card/90 backdrop-blur text-primary text-[10px] font-medium px-2.5 py-0.5 shadow-soft z-10">{p.badge}</span>
              </div>
              <div className="p-4">
                <h3 className="text-sm font-semibold text-foreground mb-0.5 group-hover:text-primary transition-colors">{p.name}</h3>
                <p className="text-[11px] text-muted-foreground mb-3 line-clamp-1">{p.desc}</p>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-display font-semibold text-foreground">{p.isQuote ? 'Custom quote' : (p.priceDisplay || <>${p.price}<span className="text-[10px] font-sans font-normal text-muted-foreground">{p.unit}</span></>)}</span>
                  {p.reviews > 0 ? (
                    <div className="flex items-center gap-0.5">
                      <Star size={11} weight="fill" className="text-amber-400" />
                      <span className="text-[11px] text-muted-foreground">{p.rating} ({p.reviews})</span>
                    </div>
                  ) : (
                    <span className="text-[10px] text-muted-foreground">New</span>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
        <TrustBar className="mt-8" />
      </Reveal>
      )}

      {/* ═══ HOW IT WORKS ═══ */}
      <Reveal as="section" className="bg-secondary/40 border-y border-border" data-testid="how-it-works">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          <div className="text-center mb-16">
            <p className="text-sm font-medium tracking-widest uppercase text-primary mb-2">How the AI planner works</p>
            <h2 className="text-4xl md:text-5xl font-display font-semibold tracking-tight text-foreground leading-[1.08]">One chat. Your whole event, planned.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              { step: '01', icon: <Sparkle size={28} weight="fill" className="text-primary" />, title: 'Describe it', desc: 'Type your event into the chat — birthday for 40, $3,500 budget, need a DJ, catering, and a photographer. One message is enough.' },
              { step: '02', icon: <Lightning size={28} weight="fill" className="text-primary" />, title: 'AI builds your plan', desc: 'In seconds you get a full plan: the right vendor for each role, matched to your budget and confirmed available on your exact date.' },
              { step: '03', icon: <CalendarCheck size={28} weight="fill" className="text-primary" />, title: 'Book it all at once', desc: 'Swap anyone you like, then confirm every vendor together in a single secure checkout. No threads, no back-and-forth.' },
            ].map((s, i) => (
              <div key={i} className="text-center fade-up" style={{ animationDelay: `${i * 50}ms` }}>
                <div className="w-16 h-16 bg-card border border-border/70 rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-soft">
                  {s.icon}
                </div>
                <div className="text-[11px] font-medium uppercase tracking-widest text-muted-foreground mb-2">Step {s.step}</div>
                <h3 className="text-2xl font-display font-semibold tracking-tight text-foreground mb-2">{s.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed max-w-xs mx-auto">{s.desc}</p>
              </div>
            ))}
          </div>
          {/* Connector line */}
          <div className="hidden md:flex items-center justify-center mt-10 gap-3">
            <div className="h-px w-20 bg-border" />
            <ArrowRight size={14} className="text-primary/60" />
            <div className="h-px w-20 bg-border" />
            <ArrowRight size={14} className="text-primary/60" />
            <div className="h-px w-20 bg-border" />
          </div>
        </div>
      </Reveal>

      {/* ═══ CATEGORIES ═══ */}
      <Reveal as="section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28" data-testid="categories-section">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="text-sm font-medium tracking-widest uppercase text-primary mb-2">Explore</p>
            <h2 className="text-4xl md:text-5xl font-display font-semibold tracking-tight text-foreground">Browse by category</h2>
          </div>
          <Link to="/browse" className="text-sm font-medium text-primary hover:text-primary/80 transition-colors hidden md:flex items-center gap-1 cursor-pointer">View all <ArrowRight size={14} /></Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {CATEGORIES.map((cat, i) => (
            <Link key={cat.id} to={cat.link || catHref(cat.name)} className="group relative overflow-hidden aspect-[4/3] rounded-2xl shadow-soft card-lift cursor-pointer fade-up" style={{ animationDelay: `${i * 50}ms` }} data-testid={`category-card-${cat.id}`}>
              <img src={cat.img} alt={cat.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 via-foreground/20 to-transparent" />
              <div className="absolute bottom-3 left-3 flex items-center gap-1.5">
                {cat.Icon && <cat.Icon size={16} weight="regular" className="text-white" />}
                <span className="text-white font-medium text-xs">{cat.name}</span>
              </div>
            </Link>
          ))}
        </div>
      </Reveal>

      {/* ═══ FEATURES ═══ */}
      <Reveal as="section" className="bg-foreground text-background" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          <div className="text-center mb-16">
            <p className="text-sm font-medium tracking-widest uppercase text-secondary/80 mb-2">Why Bookr</p>
            <h2 className="text-4xl md:text-5xl font-display font-semibold tracking-tight">Built for trust</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: <Lock size={20} className="text-secondary" />, title: 'Secure escrow', desc: 'Funds held until service is complete. Full refund guarantee.' },
              { icon: <Lightning size={20} weight="fill" className="text-secondary" />, title: 'AI planner', desc: 'Describe your event — AI builds the full package instantly.' },
              { icon: <Checks size={20} className="text-secondary" />, title: 'Verified only', desc: 'Every provider is ID + portfolio reviewed. No exceptions.' },
              { icon: <CurrencyDollar size={20} className="text-secondary" />, title: 'Transparent pricing', desc: 'No hidden fees. See the exact breakdown before you book.' },
            ].map((f, i) => (
              <div key={i} className="rounded-2xl border border-background/10 bg-background/5 p-6 hover:border-background/25 hover:bg-background/10 transition-colors duration-300 fade-up" style={{ animationDelay: `${i * 50}ms` }}>
                <div className="mb-4">{f.icon}</div>
                <h3 className="text-lg font-display font-semibold mb-1.5">{f.title}</h3>
                <p className="text-sm text-background/70 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </Reveal>

      {/* ═══ PROVIDER CTA ═══ */}
      <Reveal as="section" className="bg-secondary/40 border-b border-border" data-testid="provider-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          <div className="max-w-2xl mx-auto text-center">
            <p className="text-sm font-medium tracking-widest uppercase text-primary mb-2">For providers</p>
            <h2 className="text-4xl md:text-5xl font-display font-semibold tracking-tight text-foreground mb-5 leading-[1.08]">Grow your business on Bookr</h2>
            <p className="text-base text-muted-foreground mb-10 max-w-lg mx-auto leading-relaxed">No monthly fees. Only 6% commission when you earn. AI sends clients to you. Founding providers get priority placement.</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
              {[
                { Icon: CurrencyDollar, title: '6% only', desc: 'No subscriptions' },
                { Icon: DeviceMobile, title: 'Clients find you', desc: 'No marketing needed' },
                { Icon: CreditCard, title: 'Fast payouts', desc: 'After service complete' },
                { Icon: Robot, title: 'AI referrals', desc: 'AI recommends you' },
              ].map((p, i) => (
                <div key={i} className="bg-card rounded-2xl border border-border/70 shadow-soft card-lift p-4 text-center fade-up" style={{ animationDelay: `${i * 50}ms` }}>
                  <p.Icon size={22} weight="regular" className="text-primary mx-auto mb-1.5" />
                  <h4 className="text-sm font-semibold text-foreground">{p.title}</h4>
                  <p className="text-[11px] text-muted-foreground">{p.desc}</p>
                </div>
              ))}
            </div>
            <button onClick={goProvider} className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-xl px-6 h-11 text-sm font-semibold btn-anim shadow-soft hover:shadow-lift hover:bg-primary/90 cursor-pointer" data-testid="provider-cta-btn">
              Apply as a provider <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </Reveal>

      {/* ═══ FOOTER ═══ */}
      <footer className="bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col md:flex-row justify-between items-start gap-8">
            <div>
              <h3 className="text-xl font-display font-semibold text-foreground">Bookr<span className="text-accent-coral">.ai</span></h3>
              <p className="text-xs text-muted-foreground mt-1.5">Book any service, anywhere.</p>
              {/* Real human support — a form that reaches Milo directly */}
              <button onClick={() => openSupportForm()} data-testid="footer-support-cta"
                className="mt-4 inline-flex items-center gap-2 bg-card border-2 border-primary/25 text-primary rounded-full px-5 h-11 text-sm font-semibold btn-anim shadow-soft hover:bg-secondary hover:border-primary/40 cursor-pointer">
                <Headset size={17} weight="fill" /> Contact support
              </button>
              <p className="text-[11px] text-muted-foreground mt-2">
                Need a human? <a href={supportMailto('Bookr.ai support')} className="text-primary hover:underline">{SUPPORT_EMAIL}</a>
              </p>
            </div>
            <div className="flex gap-12 text-sm">
              <div className="space-y-2">
                <p className="text-[11px] font-medium uppercase tracking-widest text-muted-foreground">Platform</p>
                <Link to="/browse" className="block text-foreground/70 hover:text-primary transition-colors text-xs cursor-pointer">Browse</Link>
                <Link to={user ? "/ai-planner" : "/auth"} className="block text-foreground/70 hover:text-primary transition-colors text-xs cursor-pointer">AI Planner</Link>
                <Link to="/invoices" className="block text-foreground/70 hover:text-primary transition-colors text-xs cursor-pointer">Invoices</Link>
              </div>
              <div className="space-y-2">
                <p className="text-[11px] font-medium uppercase tracking-widest text-muted-foreground">Company</p>
                <Link to="/trust" className="block text-foreground/70 hover:text-primary transition-colors text-xs cursor-pointer">Trust & Safety</Link>
                <Link to="/trust" className="block text-foreground/70 hover:text-primary transition-colors text-xs cursor-pointer">The Bookr Guarantee</Link>
                <Link to="/auth?mode=register&role=provider" className="block text-foreground/70 hover:text-primary transition-colors text-xs cursor-pointer">Providers</Link>
                <Link to="/profile" className="block text-foreground/70 hover:text-primary transition-colors text-xs cursor-pointer">Settings</Link>
              </div>
              <div className="space-y-2">
                <p className="text-[11px] font-medium uppercase tracking-widest text-muted-foreground">Legal</p>
                <Link to="/legal/terms" className="block text-foreground/70 hover:text-primary transition-colors text-xs cursor-pointer">Terms of Service</Link>
                <Link to="/legal/privacy" className="block text-foreground/70 hover:text-primary transition-colors text-xs cursor-pointer">Privacy Policy</Link>
                <Link to="/legal/cancellation" className="block text-foreground/70 hover:text-primary transition-colors text-xs cursor-pointer">Cancellation Policy</Link>
              </div>
            </div>
          </div>
          <div className="border-t border-border mt-8 pt-5 text-[11px] text-muted-foreground">2026 Bookr.ai. All rights reserved.</div>
        </div>
      </footer>
    </div>
  );
}
